import { CheckCircle, ChevronLeft, ChevronRight, Download, FileText, Loader, Mail, Maximize, Minimize, PenTool, Save, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import SignatureCanvas from '../components/SignatureCanvas';
import { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';
import './Contratos.css';

const Contratos = () => {
  const { authenticatedFetch } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [contractHTML, setContractHTML] = useState(null);
  const [loadingHTML, setLoadingHTML] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [fullscreenPreview, setFullscreenPreview] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [signature, setSignature] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [savingContract, setSavingContract] = useState(false);
  const [savingAndSending, setSavingAndSending] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null); // ID del contrato que se está enviando
  
  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalBookings, setTotalBookings] = useState(0);

  // Cargar reservas con paginación
  const fetchBookings = async (page = 1, limit = itemsPerPage) => {
    try {
      setLoading(true);
      // Obtener reservas con paginación
      const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.BOOKINGS}?page=${page}&limit=${limit}`));
      const data = await response.json();

      if (data.success) {
        // Mostrar todas las reservas, no solo las confirmadas
        setBookings(data.data.bookings || []);
        setTotalBookings(data.data.pagination?.total || 0);
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('Error al cargar las reservas');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cargar reservas cuando cambia la página (solo si no hay búsqueda)
  useEffect(() => {
    if (!searchTerm) {
      fetchBookings(currentPage, itemsPerPage);
    }
  }, [currentPage]);

  // Cuando se inicia la búsqueda, cargar todas las reservas para buscar
  useEffect(() => {
    if (searchTerm) {
      fetchBookings(1, 1000);
      setCurrentPage(1); // Resetear a página 1 cuando se busca
    }
  }, [searchTerm]);

  // Cargar reservas iniciales
  useEffect(() => {
    fetchBookings(1, itemsPerPage);
  }, []);

  // Cargar contratos de la reserva seleccionada
  const fetchContracts = async (bookingId) => {
    if (!bookingId) return;
    
    try {
      setLoadingContracts(true);
      const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.CONTRACTS}/${bookingId}`));
      const data = await response.json();

      if (data.success) {
        setContracts(data.data || []);
      } else {
        setContracts([]);
      }
    } catch (error) {
      console.error('Error al cargar contratos:', error);
      setContracts([]);
    } finally {
      setLoadingContracts(false);
    }
  };

  // Cargar contratos cuando se selecciona una reserva
  useEffect(() => {
    if (selectedBookingId) {
      fetchContracts(selectedBookingId);
    } else {
      setContracts([]);
    }
  }, [selectedBookingId]);

  // Descargar contrato firmado guardado
  const downloadSignedContract = async (contract) => {
    try {
      // Extraer información del filePath
      // Formato: storage/contracts/YYYY/MM/booking_XXX/filename.pdf
      const pathParts = contract.filePath.split('/');
      const filename = pathParts[pathParts.length - 1];
      const bookingId = pathParts[pathParts.length - 2]?.replace('booking_', '');
      const month = pathParts[pathParts.length - 3];
      const year = pathParts[pathParts.length - 4];

      let downloadUrl;
      if (year && month && bookingId && filename) {
        downloadUrl = `${API_ENDPOINTS.CONTRACTS}/download/${year}/${month}/${bookingId}/${filename}`;
      } else {
        // Fallback: usar solo el filename
        downloadUrl = `${API_ENDPOINTS.CONTRACTS}/download/${filename}`;
      }

      const response = await authenticatedFetch(buildApiUrl(downloadUrl));
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `contract_${contract.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const data = await response.json();
        setError(data.message || 'Error al descargar el contrato');
      }
    } catch (error) {
      setError('Error al descargar el contrato');
      console.error('Error:', error);
    }
  };

  // Visualizar contrato firmado en nueva pestaña
  const viewSignedContract = async (contract) => {
    try {
      // Extraer información del filePath
      const pathParts = contract.filePath.split('/');
      const filename = pathParts[pathParts.length - 1];
      const bookingId = pathParts[pathParts.length - 2]?.replace('booking_', '');
      const month = pathParts[pathParts.length - 3];
      const year = pathParts[pathParts.length - 4];

      let viewUrl;
      if (year && month && bookingId && filename) {
        viewUrl = `${API_ENDPOINTS.CONTRACTS}/download/${year}/${month}/${bookingId}/${filename}`;
      } else {
        viewUrl = `${API_ENDPOINTS.CONTRACTS}/download/${filename}`;
      }

      // Usar authenticatedFetch para incluir el token de autenticación
      const response = await authenticatedFetch(buildApiUrl(viewUrl));
      
      if (response.ok) {
        // Verificar que el contenido sea un PDF
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/pdf')) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          // Limpiar el URL después de un tiempo para liberar memoria
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } else {
          // Si no es PDF, puede ser un JSON de error
          const data = await response.json();
          setError(data.message || 'Error al visualizar el contrato');
        }
      } else {
        // Intentar obtener el mensaje de error
        try {
          const data = await response.json();
          setError(data.message || 'Error al visualizar el contrato');
        } catch {
          setError(`Error ${response.status}: No se pudo visualizar el contrato`);
        }
      }
    } catch (error) {
      setError('Error al visualizar el contrato');
      console.error('Error:', error);
    }
  };

  // Enviar contrato firmado por correo
  const sendContractByEmail = async (contract) => {
    try {
      setSendingEmail(contract.id);
      setError(null);

      const response = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.CONTRACTS}/contract/${contract.id}/send-email`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Contrato enviado por correo exitosamente');
      } else {
        setError(data.message || 'Error al enviar el contrato por correo');
      }
    } catch (error) {
      setError('Error al enviar el contrato por correo');
      console.error('Error:', error);
    } finally {
      setSendingEmail(null);
    }
  };

  // Cargar HTML del contrato (sin o con firma para previsualización)
  // customSignature permite pasar una firma recién generada sin depender del estado asíncrono
  const loadContractHTML = async (bookingId, withSignature = false, customSignature = null) => {
    try {
      setLoadingHTML(true);
      setError(null);

      const effectiveSignature = customSignature ?? signature;
      
      // Verificar que tenemos la firma si se solicita
      if (withSignature && !effectiveSignature) {
        setError('No hay firma disponible. Por favor, firma el contrato primero.');
        setLoadingHTML(false);
        return;
      }
      
      const url = withSignature && effectiveSignature
        ? `${API_ENDPOINTS.CONTRACTS}/${bookingId}/html?signature=${encodeURIComponent(effectiveSignature)}`
        : `${API_ENDPOINTS.CONTRACTS}/${bookingId}/html`;
      
      const response = await authenticatedFetch(buildApiUrl(url));
      const data = await response.json();

      if (data.success) {
        setContractHTML(data.html);
        setShowPreview(true);
      } else {
        setError(data.message || 'Error al cargar el contrato');
      }
    } catch (error) {
      setError('Error al cargar el contrato');
      console.error('Error al cargar el contrato:', error);
    } finally {
      setLoadingHTML(false);
    }
  };

  // Descargar PDF (con firma si está disponible)
  const downloadPDF = async (bookingId) => {
    try {
      const response = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.CONTRACTS}/${bookingId}/generate`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            signature: signature || null
          })
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contract_${bookingId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const data = await response.json();
        setError(data.message || 'Error al descargar el PDF');
      }
    } catch (error) {
      setError('Error al descargar el PDF');
      console.error('Error:', error);
    }
  };

  // Guardar contrato con firma en el servidor
  const saveContractWithSignature = async (bookingId) => {
    if (!signature) {
      setError('Por favor, firma el contrato antes de guardarlo');
      return;
    }

    try {
      setSavingContract(true);
      setError(null);
      
      const response = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.CONTRACTS}/${bookingId}/save`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            signature: signature
          })
        }
      );

      const data = await response.json();

      if (data.success && data.data && data.data.id) {
        const newContractId = data.data.id;
        setError(null);
        alert('Contrato guardado exitosamente con firma');
        
        // Si hay una vista previa activa, recargarla con la firma
        if (showPreview && selectedBookingId && signature) {
          // Recargar el contrato con la firma para mostrarlo firmado
          await loadContractHTML(selectedBookingId, true);
        }
        
        // Recargar la lista de contratos para mostrar el nuevo contrato guardado
        if (selectedBookingId) {
          fetchContracts(selectedBookingId);
        }

        // Abrir el contrato recién guardado en una nueva pestaña
        try {
          const contractResponse = await authenticatedFetch(
            buildApiUrl(`${API_ENDPOINTS.CONTRACTS}/contract/${newContractId}`)
          );
          const contractData = await contractResponse.json();
          if (contractData.success && contractData.data) {
            await viewSignedContract(contractData.data);
          }
        } catch (viewError) {
          console.error('Error al abrir el contrato guardado:', viewError);
        }
      } else {
        setError(data.message || 'Error al guardar el contrato');
      }
    } catch (error) {
      setError('Error al guardar el contrato');
      console.error('Error:', error);
    } finally {
      setSavingContract(false);
    }
  };

  // Guardar contrato con firma y enviarlo por correo
  const saveAndSendContractWithSignature = async (bookingId) => {
    if (!signature) {
      setError('Por favor, firma el contrato antes de guardar y enviar');
      return;
    }

    try {
      setSavingAndSending(true);
      setError(null);

      // Primero guardar el contrato con firma
      const saveResponse = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.CONTRACTS}/${bookingId}/save`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            signature: signature
          })
        }
      );

      const saveData = await saveResponse.json();

      if (!saveData.success || !saveData.data || !saveData.data.id) {
        setError(saveData.message || 'Error al guardar el contrato antes de enviarlo');
        return;
      }

      const newContractId = saveData.data.id;

      // Enviar el contrato recién guardado por correo
      const emailResponse = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.CONTRACTS}/contract/${newContractId}/send-email`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      const emailData = await emailResponse.json();

      if (emailData.success) {
        alert('Contrato guardado y enviado por correo exitosamente');

        // Recargar la lista de contratos para reflejar el nuevo contrato como enviado
        if (selectedBookingId) {
          fetchContracts(selectedBookingId);
        }

        // Si hay una vista previa activa, recargarla con la firma
        if (showPreview && selectedBookingId && signature) {
          await loadContractHTML(selectedBookingId, true);
        }

        // Abrir el contrato recién guardado en una nueva pestaña
        try {
          const contractResponse = await authenticatedFetch(
            buildApiUrl(`${API_ENDPOINTS.CONTRACTS}/contract/${newContractId}`)
          );
          const contractData = await contractResponse.json();
          if (contractData.success && contractData.data) {
            await viewSignedContract(contractData.data);
          }
        } catch (viewError) {
          console.error('Error al abrir el contrato guardado tras enviar:', viewError);
        }
      } else {
        setError(emailData.message || 'Error al enviar el contrato por correo');
      }
    } catch (error) {
      setError('Error al guardar y enviar el contrato por correo');
      console.error('Error al guardar y enviar el contrato por correo:', error);
    } finally {
      setSavingAndSending(false);
    }
  };

  // Manejar cambio de firma
  const handleSignatureChange = (newSignature) => {
    setSignature(newSignature);
    // Si hay una reserva seleccionada y hay firma, mostrar/actualizar la vista previa con la firma.
    // Usamos newSignature directamente para evitar problemas con la actualización asíncrona del estado.
    if (selectedBookingId && newSignature) {
      setShowPreview(true);
      loadContractHTML(selectedBookingId, true, newSignature);
    }
  };

  // Filtrar reservas por término de búsqueda (búsqueda local en las reservas cargadas)
  const filteredBookings = bookings.filter(booking => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const customerName = (booking.customer?.fullName || booking.guestName || '').toLowerCase();
    const customerEmail = (booking.customer?.email || booking.guestEmail || '').toLowerCase();
    return (
      booking.id.toString().includes(search) ||
      customerName.includes(search) ||
      customerEmail.includes(search)
    );
  });

  // Calcular paginación
  const totalPages = searchTerm 
    ? Math.ceil(filteredBookings.length / itemsPerPage)
    : Math.ceil(totalBookings / itemsPerPage);
  
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  // Si hay búsqueda, paginar los resultados filtrados localmente
  // Si no hay búsqueda, las reservas ya vienen paginadas del servidor
  const paginatedBookings = searchTerm 
    ? filteredBookings.slice(startIndex, endIndex)
    : filteredBookings;

  // Manejar cambio de página
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    // Si no hay búsqueda, recargar desde el servidor
    if (!searchTerm) {
      fetchBookings(newPage);
    }
  };


  const selectedBooking = bookings.find(b => b.id === selectedBookingId);

  return (
    <div className={`contratos-container ${fullscreenPreview ? 'fullscreen-mode' : ''}`}>
      {!fullscreenPreview && (
        <div className="contratos-header">
          <h1>
            <FileText size={24} />
            Generar Contratos
          </h1>
        </div>
      )}

      <div className="contratos-content">
        {/* Panel izquierdo: Lista de reservas */}
        {!fullscreenPreview && (
          <div className="contratos-sidebar">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Buscar por ID, nombre o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="loading-state">
              <Loader className="spinner" />
              <p>Cargando reservas...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
            </div>
          ) : (
            <>
              <div className="bookings-list">
                {paginatedBookings.length === 0 ? (
                  <div className="empty-state">
                    <p>{searchTerm ? 'No se encontraron reservas' : 'No hay reservas disponibles'}</p>
                  </div>
                ) : (
                  paginatedBookings.map((booking) => (
                    <div
                      key={booking.id}
                      className={`booking-item ${selectedBookingId === booking.id ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedBookingId(booking.id);
                        setShowPreview(false);
                        setContractHTML(null);
                        setSignature(null); // Limpiar firma al cambiar de reserva
                        setFullscreenPreview(false); // Desactivar pantalla completa al cambiar de reserva
                      }}
                    >
                      <div className="booking-info">
                        <div className="booking-id">Reserva #{booking.id}</div>
                        <div className="booking-customer">
                          {booking.customer?.fullName || booking.guestName || 'Sin nombre'}
                        </div>
                        <div className="booking-email">
                          {booking.customer?.email || booking.guestEmail || 'Sin email'}
                        </div>
                        <div className="booking-dates">
                          {new Date(booking.startDateTime).toLocaleDateString()} -{' '}
                          {new Date(booking.endDateTime).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Controles de paginación */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    title="Página anterior"
                  >
                    <ChevronLeft 
                      size={18} 
                      strokeWidth={2.5} 
                      className="pagination-icon"
                      style={{ color: '#000000', stroke: '#000000' }}
                    />
                  </button>
                  <span className="pagination-info">
                    Página {currentPage} de {totalPages}
                    {searchTerm && ` (${filteredBookings.length} resultado${filteredBookings.length !== 1 ? 's' : ''})`}
                  </span>
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    title="Página siguiente"
                  >
                    <ChevronRight 
                      size={18} 
                      strokeWidth={2.5} 
                      className="pagination-icon"
                      style={{ color: '#000000', stroke: '#000000' }}
                    />
                  </button>
                </div>
              )}
            </>
          )}
          </div>
        )}

        {/* Panel derecho: Vista previa y acciones */}
        <div className={`contratos-main ${fullscreenPreview ? 'fullscreen-main' : ''}`}>
          {!selectedBookingId ? (
            <div className="empty-preview">
              <FileText size={48} />
              <p>Selecciona una reserva para generar el contrato</p>
            </div>
          ) : (
            <div className="preview-section">
              {!fullscreenPreview && (
                <div className="preview-header">
                <div className="preview-info">
                  <h2>Contrato - Reserva #{selectedBookingId}</h2>
                  {selectedBooking && (
                    <div className="booking-details">
                      <span>
                        {selectedBooking.customer?.fullName || selectedBooking.guestName || 'Sin nombre'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="preview-actions">
                  <button
                    className="btn-preview"
                    onClick={() => loadContractHTML(selectedBookingId, !!signature)}
                    disabled={loadingHTML}
                    title="Ver contrato sin firma"
                  >
                    {loadingHTML ? (
                      <>
                        <Loader className="spinner-small" />
                        Cargando...
                      </>
                    ) : (
                      <>
                        <FileText size={18} />
                        Ver Vista Previa
                      </>
                    )}
                  </button>
                  {showPreview && contractHTML && (
                    <button
                      className="btn-fullscreen"
                      onClick={() => setFullscreenPreview(!fullscreenPreview)}
                      title={fullscreenPreview ? "Salir de pantalla completa" : "Pantalla completa"}
                    >
                      {fullscreenPreview ? (
                        <>
                          <Minimize size={18} />
                          Salir de Pantalla Completa
                        </>
                      ) : (
                        <>
                          <Maximize size={18} />
                          Pantalla Completa
                        </>
                      )}
                    </button>
                  )}
                  {signature && (
                    <button
                      className="btn-preview-signed"
                      onClick={() => loadContractHTML(selectedBookingId, true)}
                      disabled={loadingHTML}
                      title="Ver contrato con firma"
                    >
                      <FileText size={18} />
                      Ver con Firma
                    </button>
                  )}
                  <button
                    className="btn-signature"
                    onClick={() => setShowSignatureModal(true)}
                  >
                    <PenTool size={18} />
                    {signature ? 'Cambiar Firma' : 'Firmar'}
                  </button>
                  <button
                    className="btn-download"
                    onClick={() => downloadPDF(selectedBookingId)}
                  >
                    <Download size={18} />
                    Descargar PDF
                  </button>
                  <button
                    className="btn-save"
                    onClick={() => saveContractWithSignature(selectedBookingId)}
                    disabled={!signature || savingContract}
                  >
                    {savingContract ? (
                      <>
                        <Loader className="spinner-small" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save size={18} />
                        Guardar con Firma
                      </>
                    )}
                  </button>
                </div>
              </div>
              )}

              {showPreview && contractHTML && (
                <div className={`preview-content ${fullscreenPreview ? 'fullscreen' : ''}`}>
                  <div className="preview-iframe-container">
                    {fullscreenPreview && (
                      <div className="fullscreen-header">
                        <div className="fullscreen-info">
                          <h3>Contrato - Reserva #{selectedBookingId}</h3>
                          {selectedBooking && (
                            <span>
                              {selectedBooking.customer?.fullName || selectedBooking.guestName || 'Sin nombre'}
                            </span>
                          )}
                        </div>
                        <div className="fullscreen-actions">
                          {signature && (
                            <button
                              className="btn-preview-signed-fullscreen"
                              onClick={() => loadContractHTML(selectedBookingId, true)}
                              disabled={loadingHTML}
                              title="Ver contrato con firma"
                            >
                              <FileText size={18} />
                              Ver con Firma
                            </button>
                          )}
                          <button
                            className="btn-signature-fullscreen"
                            onClick={() => setShowSignatureModal(true)}
                            title={signature ? "Cambiar firma" : "Firmar contrato"}
                          >
                            <PenTool size={18} />
                            {signature ? 'Cambiar Firma' : 'Firmar'}
                          </button>
                          <button
                            className="btn-download-fullscreen"
                            onClick={() => downloadPDF(selectedBookingId)}
                            title="Descargar PDF"
                          >
                            <Download size={18} />
                            Descargar PDF
                          </button>
                          {signature && (
                            <button
                              className="btn-save-fullscreen"
                              onClick={() => saveContractWithSignature(selectedBookingId)}
                              disabled={!signature || savingContract}
                              title="Guardar contrato con firma"
                            >
                              {savingContract ? (
                                <>
                                  <Loader className="spinner-small" />
                                  Guardando...
                                </>
                              ) : (
                                <>
                                  <Save size={18} />
                                  Guardar
                                </>
                              )}
                            </button>
                          )}
                          {signature && (
                            <button
                              className="btn-save-fullscreen"
                              onClick={() => saveAndSendContractWithSignature(selectedBookingId)}
                              disabled={!signature || savingAndSending}
                              title="Guardar contrato con firma y enviar por correo"
                            >
                              {savingAndSending ? (
                                <>
                                  <Loader className="spinner-small" />
                                  Guardando y enviando...
                                </>
                              ) : (
                                <>
                                  <Mail size={18} />
                                  Guardar y Enviar
                                </>
                              )}
                            </button>
                          )}
                          <button
                            className="btn-exit-fullscreen"
                            onClick={() => setFullscreenPreview(false)}
                            title="Salir de pantalla completa"
                          >
                            <Minimize size={18} />
                            Salir
                          </button>
                        </div>
                      </div>
                    )}
                    <iframe
                      srcDoc={contractHTML}
                      title="Vista previa del contrato"
                      className="preview-iframe"
                    />
                  </div>
                </div>
              )}

              {showPreview && !contractHTML && !loadingHTML && (
                <div className="empty-preview">
                  <p>No se pudo cargar la vista previa</p>
                </div>
              )}

              {!fullscreenPreview && signature && (
                <div className="signature-status">
                  <p>✓ Contrato firmado</p>
                </div>
              )}

              {/* Sección de contratos firmados guardados */}
              {!fullscreenPreview && selectedBookingId && (
                <div className="signed-contracts-section">
                  <h3>Contratos Firmados Guardados</h3>
                  {loadingContracts ? (
                    <div className="loading-state">
                      <Loader className="spinner-small" />
                      <p>Cargando contratos...</p>
                    </div>
                  ) : contracts.length > 0 ? (
                    <div className="contracts-list">
                      {contracts
                        .filter(contract => contract.status === 'SIGNED')
                        .map((contract) => (
                          <div key={contract.id} className="contract-item">
                            <div className="contract-info">
                              <FileText size={20} />
                              <div>
                                <p className="contract-name">
                                  Reserva #{selectedBookingId} - Contrato #{contract.id}
                                  {contract.vehicle && (
                                    <span className="contract-vehicle">
                                      {' '}- {contract.vehicle.model?.name} ({contract.vehicle.serialNumber})
                                    </span>
                                  )}
                                </p>
                                <p className="contract-date">
                                  Firmado: {contract.signedAt 
                                    ? new Date(contract.signedAt).toLocaleDateString('es-ES', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })
                                    : 'N/A'}
                                </p>
                                {contract.emailSent && (
                                  <p className="contract-email-sent">
                                    <CheckCircle size={14} />
                                    {' '}Correo enviado{contract.emailSentAt 
                                      ? ` el ${new Date(contract.emailSentAt).toLocaleDateString('es-ES', {
                                          year: 'numeric',
                                          month: 'long',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}`
                                      : ''}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="contract-actions">
                              <button
                                className="btn-view-contract"
                                onClick={() => viewSignedContract(contract)}
                                title="Visualizar contrato"
                              >
                                <FileText size={16} />
                                Ver
                              </button>
                              <button
                                className="btn-download-contract"
                                onClick={() => downloadSignedContract(contract)}
                                title="Descargar contrato"
                              >
                                <Download size={16} />
                                Descargar
                              </button>
                              <button
                                className="btn-send-email-contract"
                                onClick={() => sendContractByEmail(contract)}
                                disabled={sendingEmail === contract.id}
                                title="Enviar contrato por correo"
                              >
                                {sendingEmail === contract.id ? (
                                  <>
                                    <Loader className="spinner-small" size={16} />
                                    Enviando...
                                  </>
                                ) : (
                                  <>
                                    <Mail size={16} />
                                    Enviar
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="empty-contracts">
                      <p>No hay contratos firmados guardados para esta reserva</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showSignatureModal && (
        <SignatureCanvas
          onSignatureChange={handleSignatureChange}
          onClose={() => setShowSignatureModal(false)}
        />
      )}
    </div>
  );
};

export default Contratos;

